import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineListUlIcon from 'LuxKit/Icons/line/LineListUlIcon'
import PlaceInfoDrawer from '../components/PlaceInfoDrawer'
import { MARKER_TYPE_STOP } from 'tripPlanner/utils/mapMarkerTransforms'
import {
  RightPanel,
  RightPanelHeader,
  RightPanelContent,
  EmptyStateWrapper,
} from '../styles/layoutStyles'
import type { RightPanelView } from '../hooks/useRightPanelState'
import { useRightPanelState } from '../hooks/useRightPanelState'
import { useEventSubscription } from '../contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import MapContainer from './MapContainer'
import ItineraryContainer from './ItineraryContainer'

function RightPanelContainer() {
  const intl = useIntl()
  const { sendMessage } = useChatActions()
  const { itineraryData, hasItineraryData } = useItineraryContext()

  const {
    rightPanelView,
    setRightPanelView,
    selectedDayItineraryId,
    setSelectedDayItineraryId,
    selectedMarkerData,
    handleMarkerSelect,
    handleMarkerOverlayClose,
  } = useRightPanelState()

  // ── Computed state ──

  const selectedStop = useMemo(() => {
    if (!selectedDayItineraryId || !itineraryData) {
      return undefined
    }
    // Find the stop that contains this day
    for (const stop of itineraryData.stops) {
      const dayExists = stop.days.some((day: { id: number }) => day.id === selectedDayItineraryId)
      if (dayExists) {
        return stop
      }
    }
    return undefined
  }, [selectedDayItineraryId, itineraryData])

  const shouldShowMap = rightPanelView === 'map'
  const isItineraryView = rightPanelView === 'itinerary'
  const shouldShowItineraryEmptyState = isItineraryView && !hasItineraryData
  const shouldShowTripPlanCanvas = isItineraryView && hasItineraryData

  // ── View toggle ──

  const handleViewToggleWithCleanup = useCallback((view: RightPanelView) => {
    setRightPanelView(view)
  }, [setRightPanelView])

  // ── Event bus subscriptions ──

  useEventSubscription('reset_chat_state', () => {
    setRightPanelView('itinerary')
    setSelectedDayItineraryId(undefined)
    handleMarkerOverlayClose()
  })

  // ── i18n ──

  const itineraryOptionTitle = intl.formatMessage({
    id: 'ktG8P0ofvtLiBqqJLzYX',
    defaultMessage: 'Itinerary',
    description: 'Segmented option title for the itinerary view in the trip planner right panel',
  })

  const mapOptionTitle = intl.formatMessage({
    id: '3uJcGV6ZPs3TPDucD-0n',
    defaultMessage: 'Map',
    description: 'Segmented option title for the map view in the trip planner right panel',
  })

  const emptyStateHeading = intl.formatMessage({
    id: '9S1WS26ztBJ5qIocVrOD',
    defaultMessage: 'As we chat, I\u2019ll start building your trip here.',
    description: 'Empty state heading on the trip planner itinerary panel before any itinerary is created',
  })

  const emptyStateBody = intl.formatMessage({
    id: 'iKg1dzi0QzxuadaLeDj4',
    defaultMessage: 'You\u2019ll see a flexible itinerary take shape \u2014 with suggested days and experiences, ready for you to tweak, swap, or explore anytime.',
    description: 'Empty state body text on the trip planner itinerary panel before any itinerary is created',
  })

  const tripPlanningIllustrationAlt = intl.formatMessage({
    id: 'IOcq4TKUjnNxvkw51aTh',
    defaultMessage: 'Trip planning illustration',
    description: 'Alt text for the trip planning illustration image in the itinerary empty state',
  })

  return (
    <RightPanel>
      <RightPanelHeader>
        <SegmentedOptionsGroup
          inputsName="rightPanelView"
          onValueChange={handleViewToggleWithCleanup}
        >
          <SegmentedOption
            value="itinerary"
            title={itineraryOptionTitle}
            icon={<LineListUlIcon size="XS" />}
            checked={rightPanelView === 'itinerary'}
          />
          <SegmentedOption
            value="map"
            title={mapOptionTitle}
            icon={<LineMapIcon size="XS" />}
            checked={rightPanelView === 'map'}
          />
        </SegmentedOptionsGroup>
      </RightPanelHeader>
      <RightPanelContent>
        <ErrorBoundary fallback={null}>
          <MapContainer
            visible={shouldShowMap}
            selectedDayItineraryId={selectedDayItineraryId}
            selectedStop={selectedStop}
            selectedMarkerData={selectedMarkerData}
            onMarkerSelect={handleMarkerSelect}
            setRightPanelView={setRightPanelView}
            rightPanelView={rightPanelView}
          />
        </ErrorBoundary>
        {shouldShowItineraryEmptyState && (
          <EmptyStateWrapper padding={40}>
            <Group direction="vertical" gap={16} horizontalAlign="center">
              <Image id="kng038msa1ltxqwyjvj" width={200} height={200} alt={tripPlanningIllustrationAlt} format="gif" />
              <Group direction="vertical" gap={8} horizontalAlign="center">
                <Heading variant="heading5">{emptyStateHeading}</Heading>
                <BodyText variant="medium" colour="neutral-two" align="center">
                  {emptyStateBody}
                </BodyText>
              </Group>
            </Group>
          </EmptyStateWrapper>
        )}
        <ItineraryContainer
          visible={shouldShowTripPlanCanvas}
          selectedDayItineraryId={selectedDayItineraryId}
          selectedStop={selectedStop}
        />
      </RightPanelContent>
      {selectedMarkerData?.marker?.placeId && (
        <PlaceInfoDrawer
          isOpen={Boolean(selectedMarkerData)}
          onClose={handleMarkerOverlayClose}
          googlePlaceId={selectedMarkerData.marker.placeId}
          placeName={selectedMarkerData.marker.title || selectedMarkerData.marker.label || ''}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          hideAddToTrip={selectedMarkerData.marker.markerType === MARKER_TYPE_STOP}
        />
      )}
    </RightPanel>
  )
}

export default RightPanelContainer
